/**
 * ERPFinanceGrid — Finance-module-specific grid wrapper.
 *
 * Extends ERPAdvancedGrid with:
 *  - Locale-aware Dr/Cr/Amount column formatting (Indian: en-IN, International: en-US)
 *  - Fixed-width amount columns (110px — fits 9,99,99,999.99 + tabular alignment)
 *  - "Acct History" context menu → AccountHistoryModal (last 20 transactions)
 *  - "Open Ledger" context menu → caller-supplied navigateTo handler
 *
 * Architecture note:
 *   This pattern scales to ERPProcurementGrid, ERPInventoryGrid, ERPSalesGrid etc.
 *   Each domain grid wraps ERPAdvancedGrid with its own context-menu actions,
 *   cell renderers, and column defaults without polluting the base component.
 */
import React, { useState, useCallback, useMemo } from 'react';
import ERPAdvancedGrid from '../erp/ERPAdvancedGrid';
import AccountHistoryModal from './AccountHistoryModal';

const AMOUNT_FIELDS = ['DrAmount', 'Cramount', 'CrAmount', 'totalDr', 'totalCr', 'Amount'];
const AMOUNT_WIDTH  = 115; // px — fits 9,99,99,999.99 (Indian) + monospace padding
// Stable empty array — avoids new reference on every render when extraAmountFields not passed
const EMPTY_ARRAY = [];

const ERPFinanceGrid = ({
  // Finance-specific
  amountLocale = 'en-IN',   // 'en-IN' → Indian grouping (##,##,###.##) | 'en-US' → International
  onOpenLedger,             // (row) => void — called when "Open Ledger" is clicked
  // Optional extra amount field names per screen
  extraAmountFields = EMPTY_ARRAY,
  // Pass-through
  ...props
}) => {
  const [historyModal, setHistoryModal] = useState(null); // { accountId, accountName } | null

  // ── FIX: useMemo prevents new array reference on every render ──
  const amountFields = useMemo(
    () => [...AMOUNT_FIELDS, ...extraAmountFields],
    [extraAmountFields]
  );

  const fmtAmount = useCallback((v) => {
    const num = parseFloat(v);
    if (isNaN(num)) return '—';
    return num.toLocaleString(amountLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }, [amountLocale]);

  // ── Context menu handlers ──
  const handleHistory = useCallback((row) => {
    const accountId   = row.ACId || row.AccountId || row.GLId || '';
    const accountName = row.AccountName || row.GLName || accountId;
    if (!accountId) return;
    setHistoryModal({ accountId, accountName });
  }, []);

  const handleLedger = useCallback((row) => {
    if (onOpenLedger) onOpenLedger(row);
  }, [onOpenLedger]);

  // ── Cell renderer — amount columns always formatted + fixed width ──
  // Now stable: amountFields is memoized, only changes when extraAmountFields changes
  const cellRenderer = useCallback((field, value, row) => {
    if (amountFields.includes(field)) {
      const num = parseFloat(value);
      return (
        <span style={{
          fontFamily: 'var(--erp-font-mono)',
          fontSize: 11,
          fontVariantNumeric: 'tabular-nums',
          display: 'block',
          textAlign: 'right',
        }}>
          {isNaN(num) ? '—' : fmtAmount(num)}
        </span>
      );
    }
    return props.cellRenderer ? props.cellRenderer(field, value, row) : null;
  }, [amountFields, fmtAmount, props.cellRenderer]);

  // ── Cell class — keep right-align on amount columns ──
  const cellClassName = useCallback((field, row) => {
    if (amountFields.includes(field)) return 'text-right';
    return props.cellClassName ? props.cellClassName(field, row) : '';
  }, [amountFields, props.cellClassName]);

  // ── FIX: useMemo prevents new columns array reference on every render ──
  // ERPAdvancedGrid's useEffect([initialColumns]) was firing every render because
  // .map() always returns a new array reference. Now it only fires when columns actually change.
  const columns = useMemo(
    () => (props.columns || []).map(c =>
      amountFields.includes(c.field)
        ? { ...c, width: c.width || AMOUNT_WIDTH }
        : c
    ),
    [props.columns, amountFields]
  );

  return (
    <>
      <ERPAdvancedGrid
        {...props}
        columns={columns}
        onHistory={handleHistory}
        onLedger={onOpenLedger ? handleLedger : undefined}
        cellRenderer={cellRenderer}
        cellClassName={cellClassName}
      />
      {historyModal && (
        <AccountHistoryModal
          accountId={historyModal.accountId}
          accountName={historyModal.accountName}
          onClose={() => setHistoryModal(null)}
        />
      )}
    </>
  );
};

export default ERPFinanceGrid;
